import { useState, useEffect } from "react";
import { ShieldCheck, ShieldOff, Percent, Save, Store as StoreIcon, ImagePlus, X, Palette, User, Mail, Lock, Building2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { img } from "@/lib/media";
import { useAuth } from "@/context/AuthContext";
import { WebsiteKeys } from "@/components/WebsiteKeys";
import { BG_PRESETS, TEXT_PRESETS, FONT_OPTIONS } from "@/components/InvoiceShared";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const startSetup = async () => {
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const enable = async () => {
    try { await api.post("/auth/2fa/enable", { code }); toast.success("2FA enabled"); setSetup(null); setCode(""); checkAuth(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable", { code: disableCode }); toast.success("2FA disabled"); setDisableCode(""); checkAuth(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const canEditTax = user.role === "owner" || user.role === "manager";
  const [tab, setTab] = useState("profile");
  const tabs = [{ id: "profile", label: "Profile", icon: User }, { id: "security", label: "Security", icon: Lock }];
  if (canEditTax) tabs.push({ id: "company", label: "Company", icon: Building2 });

  return (
    <div className="h-full overflow-y-auto p-6">
      <PageHeader title="Settings" subtitle="Profile, security & company" />

      <div className="flex gap-1 border-b border-border mb-6 max-w-4xl">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`settings-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 h-11 text-sm font-semibold border-b-2 -mb-px transition ${tab === t.id ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
            <t.icon size={16} />{t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <Profile />}

      {tab === "security" && (
        <div className="space-y-6 max-w-xl">
          <EmailAddress />
          <ChangePassword />
          <div className="bg-white border border-border rounded-2xl p-6" data-testid="twofa-section">
            <div className="flex items-center gap-2 mb-1">
              {user.two_factor_enabled ? <ShieldCheck className="text-emerald-600" size={22} /> : <ShieldOff className="text-muted-foreground" size={22} />}
              <h3 className="font-heading text-xl font-bold">Two-factor authentication</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{user.two_factor_enabled ? "2FA is active on your account." : "Add an authenticator app for stronger security. Recommended for owners & managers."}</p>
            {!user.two_factor_enabled && !setup && (
              <button onClick={startSetup} data-testid="setup-2fa" className="h-12 px-5 rounded-xl bg-black text-white font-semibold active:scale-95">Set up 2FA</button>
            )}
            {!user.two_factor_enabled && setup && (
              <div className="space-y-4">
                <img src={setup.qr} alt="2FA QR" className="h-44 w-44 border border-border rounded-xl" data-testid="twofa-qr" />
                <p className="text-xs text-muted-foreground break-all">Manual key: <code>{setup.secret}</code></p>
                <input data-testid="enable-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit code" className="inp" />
                <button onClick={enable} data-testid="confirm-enable-2fa" className="w-full h-12 rounded-xl bg-black text-white font-semibold active:scale-95">Enable 2FA</button>
              </div>
            )}
            {user.two_factor_enabled && (
              <div className="space-y-3">
                <input data-testid="disable-code" value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter code to disable" className="inp" />
                <button onClick={disable} data-testid="disable-2fa" className="h-12 px-5 rounded-xl border border-destructive text-destructive font-semibold active:scale-95">Disable 2FA</button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "company" && (
        <div className="space-y-6">
          {canEditTax && <TaxSettings />}
          {user.role === "owner" && <StoreInfo />}
          {user.role === "owner" && <InvoiceTheme />}
          {user.role === "owner" && <WebsiteKeys />}
        </div>
      )}
    </div>
  );
}

function Profile() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState(user.avatar || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.url);
      await api.patch("/auth/profile", { avatar: data.url }); await checkAuth();
      toast.success("Photo updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try { await api.patch("/auth/profile", { name: name.trim() }); await checkAuth(); toast.success("Profile updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const statusColor = user.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500";
  return (
    <div className="bg-white border border-border rounded-2xl p-6 max-w-xl" data-testid="profile-settings">
      <h3 className="font-heading text-xl font-bold mb-1">Profile</h3>
      <p className="text-sm text-muted-foreground mb-5">Your personal account information.</p>
      <div className="flex items-center gap-4 mb-6">
        <div className="h-20 w-20 rounded-full border border-zinc-200 bg-zinc-50 overflow-hidden flex items-center justify-center shrink-0" data-testid="profile-avatar">
          {avatar ? <img src={img(avatar)} alt="avatar" className="h-full w-full object-cover" /> : <User className="text-zinc-300" size={30} />}
        </div>
        <label data-testid="profile-avatar-upload" className="h-10 px-4 rounded-xl border border-zinc-200 text-sm font-semibold flex items-center gap-2 cursor-pointer active:scale-95">
          <ImagePlus size={16} />{uploading ? "Uploading…" : "Change photo"}
          <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} disabled={uploading} />
        </label>
      </div>
      <div className="space-y-4">
        <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Full name</label>
          <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="inp mt-1.5" /></div>
        <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Email address</label>
          <div className="mt-1.5 inp flex items-center bg-zinc-50 text-zinc-600" data-testid="profile-email">{user.email}</div>
          <p className="text-xs text-zinc-400 mt-1">Change your email in the Security tab.</p></div>
        <div className="flex gap-10">
          <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Role</label>
            <div className="mt-1.5 font-semibold capitalize" data-testid="profile-role">{user.role}</div></div>
          <div><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Account status</label>
            <div className="mt-1.5"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor}`} data-testid="profile-status">{user.active ? "Active" : "Disabled"}</span></div></div>
        </div>
        <button onClick={save} disabled={saving} data-testid="profile-save" className="h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Save size={17} />{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function EmailAddress() {
  const { user, checkAuth } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail) && pw.length > 0;
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail.trim(), current_password: pw });
      toast.success("Email address updated"); setNewEmail(""); setPw(""); await checkAuth();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="email-settings">
      <div className="flex items-center gap-2 mb-1"><Mail className="text-brand" size={22} /><h3 className="font-heading text-xl font-bold">Email address</h3></div>
      <p className="text-sm text-muted-foreground mb-4">You're signed in with <b>{user.email}</b>.</p>
      <div className="space-y-3">
        <input data-testid="ce-new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New email address" className="inp" />
        <input data-testid="ce-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" className="inp" />
        <button onClick={save} disabled={saving || !valid} data-testid="ce-save" className="h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Save size={17} />{saving ? "Saving…" : "Change Email"}</button>
      </div>
    </div>
  );
}

function ChangePassword() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const lenOk = nw.length >= 8;
  const charOk = /[A-Za-z]/.test(nw) && /[0-9]/.test(nw);
  const matchOk = nw.length > 0 && nw === confirm;
  const valid = cur.length > 0 && lenOk && charOk && matchOk;
  const save = async () => {
    if (!valid) { toast.error("New password needs 8+ chars with a letter and a number, and must match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed successfully");
      setCur(""); setNw(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="change-password-settings">
      <div className="flex items-center gap-2 mb-1"><Lock className="text-brand" size={22} /><h3 className="font-heading text-xl font-bold">Password</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Change the password for <b>{user.email}</b>. It is never shown or shared.</p>
      <div className="space-y-3">
        <input type="password" data-testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className="inp" />
        <input type="password" data-testid="cp-new" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (8+ chars, letter + number)" className="inp" />
        <input type="password" data-testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="inp" />
        <button onClick={save} disabled={saving || !valid} data-testid="cp-save" className="h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Save size={17} />{saving ? "Saving…" : "Change Password"}</button>
      </div>
    </div>
  );
}

function StoreInfo() {
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/settings/store").then(({ data }) => setInfo(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/store", info); setInfo(data); toast.success("Store info saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...info, logo: data.url };
      const { data: saved } = await api.put("/settings/store", next); setInfo(saved);
      toast.success("Logo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeLogo = async () => {
    const next = { ...info, logo: "" };
    try { const { data } = await api.put("/settings/store", next); setInfo(data); toast.success("Logo removed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!info) return null;
  const F = [["name", "Store name"], ["tagline", "Tagline"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["sender_email", "Sender email (used to send invoices)"], ["website", "Website"], ["tax_number", "Tax number (HST/GST)"]];
  return (
    <div className="bg-white border border-border rounded-2xl p-6 mt-6 max-w-4xl" data-testid="store-info-settings">
      <div className="flex items-center gap-2 mb-1"><StoreIcon className="text-brand" size={22} /><h3 className="font-heading text-xl font-bold">Store / seller information</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Appears on invoices, generated PDFs and emails.</p>
      <div className="mb-5">
        <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Company logo</label>
        <div className="flex items-center gap-4 mt-2">
          <div className="h-20 w-20 rounded-xl border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="store-logo-preview">
            {info.logo ? <img src={img(info.logo)} alt="logo" className="h-full w-full object-contain p-1.5" /> : <ImagePlus className="text-zinc-300" size={26} />}
          </div>
          <div className="flex flex-wrap gap-2">
            <label data-testid="store-logo-upload" className="h-10 px-4 rounded-xl bg-brand text-white text-sm font-semibold flex items-center gap-2 cursor-pointer active:scale-95">
              <ImagePlus size={16} />{uploading ? "Uploading…" : (info.logo ? "Replace logo" : "Upload logo")}
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={uploading} />
            </label>
            {info.logo && <button type="button" data-testid="store-logo-remove" onClick={removeLogo} className="h-10 px-4 rounded-xl border border-zinc-200 text-sm font-semibold text-zinc-600 flex items-center gap-2 active:scale-95"><X size={15} />Remove</button>}
          </div>
        </div>
        <p className="text-xs text-zinc-400 mt-2">PNG or JPG, square works best. Used in the invoice header.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {F.map(([k, l]) => (<div key={k}><label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{l}</label><input data-testid={`store-${k}`} value={info[k] || ""} onChange={(e) => setInfo({ ...info, [k]: e.target.value })} className="inp mt-1.5" /></div>))}
      </div>
      <button onClick={save} disabled={saving} data-testid="store-info-save" className="mt-5 h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Save size={17} />{saving ? "Saving…" : "Save store info"}</button>
    </div>
  );
}

function TaxSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/tax/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const setProvinceField = (idx, patch) => setCfg((c) => ({ ...c, provinces: c.provinces.map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  const setComponentRate = (pi, ci, rate) => setCfg((c) => ({ ...c, provinces: c.provinces.map((p, i) => i === pi ? { ...p, components: p.components.map((comp, j) => j === ci ? { ...comp, rate: rate === "" ? "" : Number(rate) } : comp) } : p) }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...cfg, provinces: cfg.provinces.map((p) => ({ ...p, components: p.components.map((comp) => ({ ...comp, rate: Number(comp.rate) || 0 })) })) };
      const { data } = await api.put("/tax/config", clean);
      setCfg(data); toast.success("Tax settings saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!cfg) return null;

  return (
    <div className="bg-white border border-border rounded-2xl p-6 mt-6 max-w-4xl" data-testid="tax-settings">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="text-brand" size={22} />
        <h3 className="font-heading text-xl font-bold">Canadian sales tax</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Destination-based tax by shipping province. Changes here only affect future orders — past invoices keep their original rates.</p>

      <div className="flex flex-col sm:flex-row gap-4 mb-5">
        <label className="flex items-center gap-2 text-sm font-semibold" data-testid="tax-enabled-toggle">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="h-4 w-4" />
          Tax collection enabled
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold">
          Default province
          <select data-testid="tax-default-province" value={cfg.default_province} onChange={(e) => setCfg({ ...cfg, default_province: e.target.value })} className="h-9 px-2 rounded-lg border border-zinc-200">
            {cfg.provinces.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-zinc-400 border-b border-zinc-100"><th className="py-2 pr-4">Province</th><th className="py-2 pr-4">Active</th><th className="py-2">Rates (%)</th></tr></thead>
          <tbody>
            {cfg.provinces.map((p, pi) => (
              <tr key={p.code} className="border-b border-zinc-50" data-testid={`tax-row-${p.code}`}>
                <td className="py-2 pr-4 font-semibold">{p.name} <span className="text-zinc-400 font-normal">({p.code})</span></td>
                <td className="py-2 pr-4"><input type="checkbox" checked={p.enabled !== false} onChange={(e) => setProvinceField(pi, { enabled: e.target.checked })} className="h-4 w-4" data-testid={`tax-active-${p.code}`} /></td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-2">
                    {p.components.map((comp, ci) => (
                      <span key={ci} className="flex items-center gap-1 bg-zinc-50 rounded-lg px-2 py-1">
                        <span className="text-xs font-semibold text-zinc-500">{comp.name}</span>
                        <input type="number" step="0.001" value={comp.rate} onChange={(e) => setComponentRate(pi, ci, e.target.value)} data-testid={`tax-rate-${p.code}-${comp.name}`} className="w-16 h-8 px-2 rounded-md border border-zinc-200 text-right" />
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button onClick={save} disabled={saving} data-testid="tax-save" className="mt-5 h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Save size={17} />{saving ? "Saving…" : "Save tax settings"}</button>
    </div>
  );
}

function InvoiceTheme() {
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/invoice-theme").then(({ data }) => setTheme(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/invoice-theme", theme); setTheme(data); toast.success("Invoice theme saved — new invoices will use it"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const reset = () => setTheme({ header_bg: "#ffffff", text_color: "#1f2d3d", font: "Inter" });
  if (!theme) return null;
  const hb = (theme.header_bg || "#ffffff");
  // readable header text based on chosen header color
  const lum = (h) => { let s = h.replace("#", ""); if (s.length === 3) s = s.split("").map((c) => c + c).join(""); const n = parseInt(s || "ffffff", 16); const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255; const f = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)); return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b); };
  const hInk = lum(hb) > 0.5 ? "#0b2740" : "#ffffff";
  const fontStack = { Inter: "'Inter',sans-serif", Roboto: "'Roboto',sans-serif", Poppins: "'Poppins',sans-serif", "Open Sans": "'Open Sans',sans-serif", Lato: "'Lato',sans-serif", Montserrat: "'Montserrat',sans-serif", Arial: "Arial,sans-serif", Georgia: "Georgia,serif" }[theme.font] || "'Inter',sans-serif";
  const Swatches = ({ presets, value, onPick, kind }) => (
    <div className="flex flex-wrap items-center gap-2 mt-1.5">
      {presets.map((p) => (
        <button key={p.hex} type="button" data-testid={`theme-${kind}-${p.name.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => onPick(p.hex)} title={p.name}
          className={`h-9 w-9 rounded-lg border transition active:scale-90 ${(value || "").toLowerCase() === p.hex.toLowerCase() ? "border-brand ring-2 ring-brand/40" : "border-zinc-200"}`} style={{ background: p.hex }} />
      ))}
      <label className="h-9 w-9 rounded-lg border border-zinc-200 relative overflow-hidden cursor-pointer flex items-center justify-center" title="Custom color" style={{ background: value, color: kind === "text" ? "#fff" : "#64748b" }}>
        <Palette size={15} />
        <input type="color" data-testid={`theme-${kind}-custom`} value={value || "#ffffff"} onChange={(e) => onPick(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
      </label>
    </div>
  );
  return (
    <div className="bg-white border border-border rounded-2xl p-6 mt-6 max-w-4xl" data-testid="invoice-theme-settings">
      <div className="flex items-center gap-2 mb-1"><Palette className="text-brand" size={22} /><h3 className="font-heading text-xl font-bold">Invoice theme</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Sets the default colors & font for every new invoice. The preview, print, PDF and email attachment all use this exact theme. Existing invoices keep the theme they were created with.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Header / accent color</label>
            <Swatches presets={BG_PRESETS} value={theme.header_bg} onPick={(v) => setTheme({ ...theme, header_bg: v })} kind="bg" />
            <p className="text-[11px] text-zinc-400 mt-1.5">Themes the invoice header band, product-table header and total row.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Body text color</label>
            <Swatches presets={TEXT_PRESETS} value={theme.text_color} onPick={(v) => setTheme({ ...theme, text_color: v })} kind="text" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Font</label>
            <select data-testid="theme-font" value={theme.font} onChange={(e) => setTheme({ ...theme, font: e.target.value })} className="inp mt-1.5">
              {FONT_OPTIONS.map((ff) => <option key={ff} value={ff}>{ff}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Live preview</label>
          <div className="mt-1.5 rounded-xl overflow-hidden border border-zinc-200" data-testid="theme-preview" style={{ fontFamily: fontStack }}>
            <div className="px-4 py-4 flex items-center justify-between" style={{ background: hb }}>
              <span className="text-2xl font-semibold" style={{ color: hInk }}>Invoice</span>
              <span className="text-xs font-bold" style={{ color: hInk }}>#INV-0001</span>
            </div>
            <div className="p-4" style={{ color: theme.text_color }}>
              <p className="text-sm font-bold">Bill To: Acme Trucking Co.</p>
              <div className="mt-3 text-xs">
                <div className="flex items-center justify-between px-2 py-1.5 font-semibold" style={{ background: hb, color: hInk }}>
                  <span>Product</span><span>Qty</span><span>Total</span>
                </div>
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-zinc-100"><span>Brake Pad Set</span><span>2</span><span>$84.00</span></div>
                <div className="flex items-center justify-between px-2 py-1.5"><span>Oil Filter</span><span>1</span><span>$18.50</span></div>
              </div>
              <div className="flex items-center justify-between px-2 py-2 mt-2 font-extrabold" style={{ background: hb, color: hInk }}>
                <span>Total</span><span>$102.50</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-6">
        <button onClick={save} disabled={saving} data-testid="theme-save" className="h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Save size={17} />{saving ? "Saving…" : "Save invoice theme"}</button>
        <button onClick={reset} data-testid="theme-reset" className="h-11 px-5 rounded-xl border border-zinc-200 font-semibold text-zinc-600 active:scale-95">Reset to default</button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>;
}
